<template>
  <Teleport to="body">
    <div
      class="bottom-sheet"
      :class="{ 'bottom-sheet--open': isOpen }"
      :style="dragStyle"
    >
      <div
        class="bottom-sheet__handle-area"
        @click="toggle"
        @touchstart.passive="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
      >
        <div class="bottom-sheet__handle"></div>

        <div
          v-if="tabs && tabs.length"
          class="bottom-sheet__tabs-row"
          @click.stop
        >
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="bottom-sheet__tab"
            :class="{
              'bottom-sheet__tab--active': activeTab === tab.key,
            }"
            @click="onTabClick(tab.key)"
          >
            {{ tab.label }}
          </button>
          <!-- <svg
            class="bottom-sheet__chevron bottom-sheet__chevron--right"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            @click.stop="toggle"
          >
            <polyline points="18 15 12 9 6 15" />
          </svg> -->
        </div>

        <div v-else class="bottom-sheet__peek">
          <slot name="peek" />
          <svg
            class="bottom-sheet__chevron"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </div>
      </div>

      <div class="bottom-sheet__content">
        <template v-if="tabs && tabs.length">
          <div
            v-for="tab in tabs"
            :key="tab.key"
            v-show="activeTab === tab.key"
            class="bottom-sheet__tab-panel"
          >
            <slot :name="`tab-${tab.key}`" />
          </div>
        </template>
        <template v-else>
          <slot />
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script>
export default {
  name: "BaseBottomSheet",

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    peekHeight: {
      type: Number,
      default: 64,
    },
    tabs: {
      // [{ key: 'info', label: 'Информация' }, ...]
      type: Array,
      default: null,
    },
    defaultTab: {
      type: String,
      default: null,
    },
  },

  emits: ["update:modelValue", "tab-change"],

  data() {
    return {
      isOpen: this.modelValue,
      activeTab:
        this.defaultTab || (this.tabs && this.tabs[0]?.key) || null,
      dragOffset: 0,
      isDragging: false,
      touchStartY: 0,
      touchStartOpen: false,
    };
  },

  computed: {
    peekHeightPx() {
      return `${this.peekHeight}px`;
    },

    dragStyle() {
      if (!this.isDragging) return {};

      let offset = this.dragOffset;

      if (this.touchStartOpen) {
        // Открыта — можно тянуть только вниз
        offset = Math.max(0, offset);
      } else {
        // Закрыта — можно тянуть только вверх, но не дальше нуля
        offset = Math.min(0, offset);
        // Сопротивление при попытке тянуть выше допустимого предела
        if (offset < 0) {
          offset = offset * 0.4;
        }
      }

      return {
        transform: `translateY(calc(${
          this.touchStartOpen ? "0px" : `100% - ${this.peekHeight}px`
        } + ${offset}px))`,
        transition: "none",
      };
    },
  },

  watch: {
    modelValue(val) {
      this.isOpen = val;
    },
    isOpen(val) {
      this.$emit("update:modelValue", val);
    },
    tabs(val) {
      if (
        val &&
        val.length &&
        !val.find((t) => t.key === this.activeTab)
      ) {
        this.activeTab = val[0].key;
      }
    },
    defaultTab(val) {
      if (val) this.activeTab = val;
    },
  },

  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },

    onTabClick(key) {
      this.activeTab = key;
      if (!this.isOpen) this.isOpen = true;
      this.$emit("tab-change", key);
    },

    onTouchStart(e) {
      this.isDragging = true;
      this.dragOffset = 0;
      this.touchStartY = e.touches[0].clientY;
      this.touchStartOpen = this.isOpen;
    },

    onTouchMove(e) {
      if (!this.isDragging) return;

      const delta = e.touches[0].clientY - this.touchStartY;

      if (delta > 0) {
        e.preventDefault();
      }

      this.dragOffset = delta;
    },

    onTouchEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;

      const threshold = 60;

      if (this.touchStartOpen && this.dragOffset > threshold) {
        this.isOpen = false;
      } else if (
        !this.touchStartOpen &&
        this.dragOffset < -threshold
      ) {
        this.isOpen = true;
      } else {
        this.isOpen = this.touchStartOpen;
      }

      this.dragOffset = 0;
    },
  },
};
</script>

<style scoped>
.bottom-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #fff;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(calc(100% - v-bind(peekHeightPx)));
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
  height: 72dvh;
  display: none;
  flex-direction: column;
}

.bottom-sheet--open {
  transform: translateY(0);
}

.bottom-sheet__handle-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 16px 0;
  cursor: pointer;
  flex-shrink: 0;
  gap: 12px;
  min-height: 92px;
  user-select: none;
  -webkit-user-select: none;
}

.bottom-sheet__handle {
  width: 36px;
  height: 4px;
  background: #ddd;
  border-radius: 2px;
  flex-shrink: 0;
}

.bottom-sheet__peek {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.bottom-sheet__tabs-row {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 4px;
  padding-bottom: 10px;
}

.bottom-sheet__tab {
  flex: 1;
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 500;
  color: #888;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
  white-space: nowrap;
  text-align: center;
}

.bottom-sheet__tab--active {
  color: var(--color-primary, #01696f);
  border-bottom-color: var(--color-primary, #01696f);
}

.bottom-sheet__chevron {
  width: 20px;
  height: 20px;
  color: #999;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.bottom-sheet__chevron--right {
  margin-left: auto;
  cursor: pointer;
}

.bottom-sheet--open .bottom-sheet__chevron {
  transform: rotate(180deg);
}

.bottom-sheet__content {
  overflow-y: auto;
  flex: 1;
  padding: 6px 0;
  overscroll-behavior: contain;
}

.bottom-sheet__tab-panel {
  height: 100%;
}

@media screen and (max-width: 767px) {
  .bottom-sheet {
    display: flex;
  }
}
</style>
